import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import Chart from 'react-apexcharts';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCButton, SCLoader, SCPagination } from 'rollup-allspark';
import { DEFAULT_PAGE_SIZES } from '../../../config/constants/pagination';
import useAnalytics from '../../../hooks/useAnalytics';
import { formatDate } from '../../../utils/format-date';
import SectionCard from '../components/SectionCard';
import { getBaseChartOptions, mergeChartOptions } from '../theme/apexTheme';
import { COLORS } from '../theme/tokens';
import {
  loadCashShiftsData,
  loadAnalyticsCsv,
  getXLSX,
  updatePagination,
} from '../actions';
import { EXPORT_CASH_SHIFTS_CSV, EXPORT_CASH_SHIFTS_EXCEL } from '../sagaMap';
import * as selectors from '../selectors';

const COLUMN_COUNT = 10;

const formatAmount = (amount, currencySymbol = 'Rp') => {
  if (amount === null || amount === undefined || amount === '') return '—';
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(numAmount)) return '—';
  // BE returns minor units (PRD TR-2). Divide by 100 before formatting.
  const major = numAmount / 100;
  return `${currencySymbol}${Math.round(major).toLocaleString('id-ID')}`;
};

const sumField = (rows, field) =>
  rows.reduce((acc, row) => {
    const v = row[field];
    if (v === null || v === undefined) return acc;
    const n = typeof v === 'string' ? parseFloat(v) : v;
    return Number.isNaN(n) ? acc : acc + n;
  }, 0);

const SummaryCard = ({ label, value, hint }) => (
  <div className="flex-1 min-w-0 bg-white border border-grey rounded-lg p-4">
    <div className="text-sm text-grey-dark font-medium">{label}</div>
    <div className="mt-1 text-lg font-semibold text-black truncate">
      {value}
    </div>
    {hint ? <div className="mt-1 text-xs text-grey-dark">{hint}</div> : null}
  </div>
);

SummaryCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.string,
};

const CashMovementsTable = ({
  cashShiftsData,
  cashShiftsLoading,
  paginationObj,
  handleUpdatePagination,
  getCashShifts,
  handleAnalyticsCsvDownload,
  downloadXLSX,
  selectedBusiness,
  selectedCity,
  onShiftClick,
}) => {
  const { getAnalyticsParams } = useAnalytics();

  const { cashShifts = [], pagination } = cashShiftsData || {};
  const { pageNo, pageSize } = paginationObj || {};

  const fetchCashShifts = () => {
    const params = { ...getAnalyticsParams() };
    params.page_no = pageNo;
    params.page_size = pageSize;
    // Scope to the selected business — matches the Filters-bar path
    // (Filters/index.jsx onFilterApplied) so pagination/mount fetches don't
    // drop the business filter and query across all brands.
    params.business_id = selectedBusiness?.id;

    if (params.brand_id || params.branch_id) {
      getCashShifts(params);
    }
  };

  useEffect(fetchCashShifts, [paginationObj]);

  const buildExportParams = () => {
    const analyticsParams = getAnalyticsParams();
    if (!analyticsParams) return null;
    const { period_start, period_end, ...rest } = analyticsParams;
    return {
      ...rest,
      business_id: selectedBusiness?.id,
      start_date: period_start,
      end_date: period_end,
      filterByCity: selectedCity?.length
        ? selectedCity.map(c => c.id).join(',')
        : undefined,
    };
  };

  const onCsvDownloadButtonClick = () => {
    const params = buildExportParams();
    if (!params) return;
    if (!EXPORT_CASH_SHIFTS_CSV.url || !EXPORT_CASH_SHIFTS_CSV.method) return;

    handleAnalyticsCsvDownload({
      url: EXPORT_CASH_SHIFTS_CSV.url,
      method: EXPORT_CASH_SHIFTS_CSV.method,
      params,
      fileName: 'Analytics-Cash-Shifts',
    });
  };

  const onXlsxDownloadButtonClick = () => {
    const params = buildExportParams();
    if (!params) return;
    if (!EXPORT_CASH_SHIFTS_EXCEL.url || !EXPORT_CASH_SHIFTS_EXCEL.method)
      return;

    downloadXLSX({
      url: EXPORT_CASH_SHIFTS_EXCEL.url,
      method: EXPORT_CASH_SHIFTS_EXCEL.method,
      params,
      fileName: 'Analytics-Cash-Shifts',
    });
  };

  let renderRows = null;

  if (cashShiftsLoading) {
    renderRows = (
      <tr>
        <td
          rowSpan="10"
          colSpan={COLUMN_COUNT}
          className="p-5"
          style={{ height: '380px' }}
        >
          <SCLoader lineWidth={5} diameter={50} />
        </td>
      </tr>
    );
  } else if (cashShifts && cashShifts.length) {
    renderRows = cashShifts.map(shift => {
      const isOpen = shift.is_open || shift.end_cash == null;
      const symbol = shift.currency_symbol || 'Rp';
      const handleClick = () => onShiftClick && onShiftClick(shift.shift_id);

      let shiftEndCell = '-';
      if (isOpen) {
        shiftEndCell = '—';
      } else if (shift.shift_end) {
        shiftEndCell = formatDate(shift.shift_end, 'yyyy-MM-dd HH:mm');
      }

      return (
        <tr
          key={`${shift.branch_id}-${shift.shift_id}`}
          className="border-b border-grey-light hover:bg-grey-lighter cursor-pointer"
          onClick={handleClick}
        >
          <td className="sc-tabular-nums px-4 py-3 text-sm text-black">
            {shift.branch_name || '-'}
          </td>
          <td className="sc-tabular-nums px-4 py-3 text-sm text-black">
            <div className="flex items-center gap-x-2">
              <span>{shift.shift_id || '-'}</span>
              {isOpen ? (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-lighter text-green-dark font-medium">
                  Open
                </span>
              ) : null}
            </div>
          </td>
          <td className="px-4 py-3 text-xs text-black whitespace-nowrap">
            {shift.shift_start
              ? formatDate(shift.shift_start, 'yyyy-MM-dd HH:mm')
              : '-'}
          </td>
          <td className="sc-tabular-nums px-4 py-3 text-sm text-black">
            {formatAmount(shift.start_cash, symbol)}
          </td>
          <td className="sc-tabular-nums px-4 py-3 text-sm text-black">
            {formatAmount(shift.cash_sales, symbol)}
          </td>
          <td className="sc-tabular-nums px-4 py-3 text-sm text-black">
            {formatAmount(shift.change, symbol)}
          </td>
          <td className="sc-tabular-nums px-4 py-3 text-sm text-black">
            {formatAmount(shift.cash_in, symbol)}
          </td>
          <td className="sc-tabular-nums px-4 py-3 text-sm text-black">
            {formatAmount(shift.cash_out, symbol)}
          </td>
          <td className="px-4 py-3 text-xs text-black whitespace-nowrap">
            {shiftEndCell}
          </td>
          <td className="sc-tabular-nums px-4 py-3 text-sm text-black">
            {isOpen ? '—' : formatAmount(shift.end_cash, symbol)}
          </td>
        </tr>
      );
    });
  } else {
    renderRows = (
      <tr className="text-center">
        <td colSpan={COLUMN_COUNT} className="p-5 text-sm text-grey-dark">
          —
        </td>
      </tr>
    );
  }

  const renderPagination =
    !cashShiftsLoading && cashShifts && cashShifts.length ? (
      <SCPagination
        total={pagination && pagination.total ? pagination.total : 0}
        page={pageNo}
        size={pageSize}
        sizes={DEFAULT_PAGE_SIZES}
        onPageChange={handleUpdatePagination}
      />
    ) : null;

  const renderDownloadButton =
    cashShifts && cashShifts.length ? (
      <div className="flex gap-x-2">
        <SCButton
          variant="secondary"
          size="sm"
          className="h-6 w-auto px-4 text-xs bg-grey-light hover:bg-grey text-black rounded-lg"
          action={onCsvDownloadButtonClick}
        >
          <div className="flex items-center gap-x-1 font-medium">
            <i className="kt-download" />
            <p>CSV</p>
          </div>
        </SCButton>
        <SCButton
          variant="secondary"
          size="sm"
          className="h-6 w-auto px-4 text-xs bg-grey-light hover:bg-grey text-black rounded-lg"
          action={onXlsxDownloadButtonClick}
        >
          <div className="flex items-center gap-x-1 font-medium">
            <i className="kt-download" />
            <p>XLSX</p>
          </div>
        </SCButton>
      </div>
    ) : null;

  const symbolForSummary = cashShifts[0]?.currency_symbol || 'Rp';
  const closedShifts = cashShifts.filter(
    s => !(s.is_open || s.end_cash == null),
  );
  const openShiftCount = cashShifts.length - closedShifts.length;
  const totalStartCash = sumField(cashShifts, 'start_cash');
  const totalEndCash = sumField(closedShifts, 'end_cash');
  const totalCashSales = sumField(cashShifts, 'cash_sales');
  const totalCashIn = sumField(cashShifts, 'cash_in');
  const totalCashOut = sumField(cashShifts, 'cash_out');
  const netMovement = totalCashIn - totalCashOut;

  // Cash In vs Cash Out per shift (BE values are minor units → /100).
  const renderCashFlowChart =
    !cashShiftsLoading && cashShifts.length ? (
      <SectionCard title="Cash In vs Cash Out" className="mb-4">
        <Chart
          type="bar"
          height={260}
          series={[
            {
              name: 'Cash In',
              data: cashShifts.map(s => +((+s.cash_in || 0) / 100).toFixed(2)),
            },
            {
              name: 'Cash Out',
              data: cashShifts.map(s => +((+s.cash_out || 0) / 100).toFixed(2)),
            },
          ]}
          options={mergeChartOptions(getBaseChartOptions(), {
            colors: [COLORS.success, COLORS.danger],
            stroke: { width: 0 },
            plotOptions: { bar: { columnWidth: '60%', borderRadius: 4 } },
            xaxis: {
              categories: cashShifts.map(s =>
                s.shift_start
                  ? formatDate(s.shift_start, 'MMM dd')
                  : `#${s.shift_id || ''}`,
              ),
            },
            yaxis: {
              labels: {
                formatter: v =>
                  `${symbolForSummary}${Math.round(v).toLocaleString('id-ID')}`,
              },
            },
            tooltip: {
              y: {
                formatter: v =>
                  `${symbolForSummary}${Math.round(v).toLocaleString('id-ID')}`,
              },
            },
          })}
        />
      </SectionCard>
    ) : null;

  const renderSummary =
    !cashShiftsLoading && cashShifts.length ? (
      <div className="w-full flex flex-row gap-3 mb-4">
        <SummaryCard
          label="Start Cash"
          value={formatAmount(totalStartCash, symbolForSummary)}
          hint={`${cashShifts.length} Shift${
            cashShifts.length === 1 ? '' : 's'
          }`}
        />
        <SummaryCard
          label="End Cash"
          value={formatAmount(totalEndCash, symbolForSummary)}
          hint={
            openShiftCount > 0
              ? `Excludes ${openShiftCount} Open`
              : 'All Shifts Closed'
          }
        />
        <SummaryCard
          label="Cash Sales"
          value={formatAmount(totalCashSales, symbolForSummary)}
        />
        <SummaryCard
          label="Net Movement"
          value={formatAmount(netMovement, symbolForSummary)}
          hint="Cash In − Cash Out"
        />
      </div>
    ) : null;

  return (
    <div className="w-full">
      {renderSummary}
      {renderCashFlowChart}
      <SectionCard title="Cash Shifts" actions={renderDownloadButton}>
        <div className="overflow-x-auto">
          <table className="table-auto w-full">
            <thead>
              <tr>
                <th className="p-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker whitespace-nowrap text-left">
                  Branch Name
                </th>
                <th className="p-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker whitespace-nowrap text-left">
                  Shift ID
                </th>
                <th className="p-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker whitespace-nowrap text-left">
                  Shift Start
                </th>
                <th className="p-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker whitespace-nowrap text-left">
                  Start Cash
                </th>
                <th className="p-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker whitespace-nowrap text-left">
                  Cash Sales
                </th>
                <th className="p-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker whitespace-nowrap text-left">
                  Change
                </th>
                <th className="p-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker whitespace-nowrap text-left">
                  Cash In
                </th>
                <th className="p-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker whitespace-nowrap text-left">
                  Cash Out
                </th>
                <th className="p-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker whitespace-nowrap text-left">
                  Shift End
                </th>
                <th className="p-3 border-b border-grey text-xs font-semibold uppercase text-grey-darker whitespace-nowrap text-left">
                  End Cash
                </th>
              </tr>
            </thead>
            <tbody>{renderRows}</tbody>
          </table>
        </div>
        <div className="w-full flex justify-center pt-4">
          {renderPagination}
        </div>
      </SectionCard>
    </div>
  );
};

CashMovementsTable.propTypes = {
  cashShiftsData: PropTypes.object,
  cashShiftsLoading: PropTypes.bool,
  paginationObj: PropTypes.object,
  handleUpdatePagination: PropTypes.func,
  getCashShifts: PropTypes.func,
  handleAnalyticsCsvDownload: PropTypes.func,
  downloadXLSX: PropTypes.func,
  selectedBusiness: PropTypes.object,
  selectedCity: PropTypes.array,
  onShiftClick: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  cashShiftsData: selectors.makeSelectCashShiftsData(),
  cashShiftsLoading: selectors.makeSelectCashShiftsLoading(),
  paginationObj: selectors.makeSelectPaginationObj(),
  selectedBusiness: selectors.makeSelectProperty('selectedBusiness'),
  selectedCity: selectors.makeSelectProperty('selectedCities'),
});

const mapDispatchToProps = dispatch => ({
  handleUpdatePagination: (page, size) =>
    dispatch(updatePagination(page, size)),
  getCashShifts: params => dispatch(loadCashShiftsData(params)),
  handleAnalyticsCsvDownload: params => dispatch(loadAnalyticsCsv(params)),
  downloadXLSX: params => dispatch(getXLSX(params)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(CashMovementsTable);
